import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import NotificationList from "./NotificationList";
import {
  getAllNotifications,
  setNewNotification,
  showNotificationsBadge,
} from "api";
import notificationIcon from "assets/icons/notificationBellFilledIcon.svg";
import styled from "styled-components";
import { useInfiniteQuery } from "@tanstack/react-query";
import { connect } from "react-redux";
import { channelEventHandlers, usePrivateChannels } from "ws/Channels";

const Wrapper = styled.div`
  position: relative;

  .bell {
    transition: all 0.2s ease-out;
    gap: 0.6rem;
    height: 36px;
    display: flex;
    /* align-items: center; */
    padding: 8px;
    justify-content: center;

    .bell_icon {
      position: relative;

      .badge {
        z-index: 10;
        position: absolute;
        top: -2px;
        right: -3px;
        height: 10px;
        width: 10px;
        border-radius: 50%;
        background-color: #ff0000;
      }
    }

    .icon {
      height: 20px;
    }

    .text {
      color: var(--grey_6);
    }

    &:hover {
      background-color: var(--background);
    }
  }

  @media screen and (max-width: 768px) {
    .bell .btn_text {
      display: none;
    }
  }
`;

function useNotificationChannel(authUserId, onChange) {
  const channels = usePrivateChannels(authUserId);
  useEffect(() => {
    if (channels) {
      channels.listenToAll(onChange);
    }
    // eslint-disable-next-line
  }, [channels]);
}

const Notifications = ({ notifications_badge, user, brands }) => {
  const [showNotifications, setShowNotifications] = useState(false);

  const {
    data,
    isPending: isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["notifications"],
    queryFn: ({ pageParam }) => getAllNotifications(pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages.length + 1;
      return nextPage <= lastPage?.data?.data?.pagination?.meta?.last_page
        ? nextPage
        : undefined;
    },
    refetchOnWindowFocus: false,
  });

  const notifications = useMemo(() => {
    return data?.pages.flatMap((page) => page?.data?.data?.notifications || []);
  }, [data]);

  const observer = useRef(null);
  const loadMore = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage]);

  const lastListElementRef = useCallback(
    (node) => {
      if (isLoading || isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          loadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, isFetchingNextPage, loadMore, hasNextPage]
  );

  const handleBellClick = () => {
    setShowNotifications(true);
    if (!notifications_badge) return;
    showNotificationsBadge(false);
  };

  const handleNotificationsEvent = (eventName, data) => {
    refetch();
    setNewNotification(data);
    console.log(data);

    if (
      !["new_conversation_message", "new_social_comment"].includes(data.event)
    ) {
      showNotificationsBadge(true);
    }

    channelEventHandlers[data.event]?.(true, data);
  };

  useNotificationChannel(user?.id, handleNotificationsEvent);

  return (
    <Wrapper>
      <button
        className="bell notification-tour"
        title="Notifications"
        onClick={handleBellClick}
      >
        <span className="bell_icon">
          {notifications_badge && <span className="badge"></span>}
          <img className="icon" src={notificationIcon} alt="notificationIcon" />
        </span>
      </button>
      {showNotifications && (
        <NotificationList
          notifications={notifications}
          setShowNotifications={setShowNotifications}
          loading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          lastListElementRef={lastListElementRef}
          brands={brands}
        />
      )}
    </Wrapper>
  );
};

const mapStateToProps = (state) => {
  return {
    notifications_badge: state.notifications_badge,
    user: state.user,
    brands: state.brands,
  };
};

export default connect(mapStateToProps)(Notifications);
